<template>
	<template v-if="supportsResizer && !noSize">
		<div class="dynamic-img-container" ref="dynamicImgContainer" :style="{height: `${container.height}px`}">
			<div v-if="!show">
				<div class="skeleton-lines">
					<div v-for="(item, index) in getLinesCount" :key="index"></div>
				</div>
			</div>
			<img v-else :src="image"/>
		</div>
	</template>
	<template v-else>
		<img :src="image"/>
	</template>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import debounce from 'lodash/debounce';

function extractSize (url: string) {
	const match = url.match(/size=(\d+)x(\d+)/);
	if (match) {
		const height = parseInt(match[2], 10);
		const width = parseInt(match[1], 10);
		return { height, width };
	}
	
	return { height: 0, width: 0 }; // or throw an error if preferred
}

export default defineComponent({
	data () : any {
		return {
			supportsResizer: typeof window.ResizeObserver !== 'undefined',
			noSize: !extractSize(this.image).height,
			show: false,
			resizeObserver: null,
			container: {
				width: 0,
				height: 0
			}
		}
	},
	mounted () {
		if (!this.supportsResizer) {
			return;
		}

		if (this.noSize) {
			return;
		}

		this.addResize();
		this.calculateHeight()
		const img = new Image();
		img.src = this.image;
		img.onload = () => {
			this.show = true;
		}
	},
	computed: {
		getLinesCount () {
			const { height } = this.container;
			if (!height) {
				return 0
			}
			const count = Math.ceil(height / 27) || 0;
			return new Array(count).fill(0);
		}
	},
	methods: {
		addResize () {
			const cb = debounce(this.calculateHeight.bind(this), 20)
			this.resizeObserver = new ResizeObserver(cb)
			this.resizeObserver.observe(this.$refs.dynamicImgContainer as Element)
		},
		calculateHeight () {
			const containerRef = (this.$refs.dynamicImgContainer as HTMLElement)
			const containerSize = containerRef.getBoundingClientRect()
			const imageSize = extractSize(this.image);
			if (containerSize.width >= imageSize?.width) {
				this.container.height = imageSize.height
			} else {
				const reductionRatio = containerSize.width / imageSize?.width;
				this.container.height = imageSize.height * reductionRatio
			}
		}
	},
	props: {
		image: {
			type: String,
			required: true
		}
	},
	unmounted () {
		this.resizeObserver && this.resizeObserver.disconnect();
	}
})
</script>

<style scoped>
	.dynamic-img-container{
		overflow: hidden;
	}
	.dynamic-img-container img{
		height: 100%;
	}
</style>
